import MatomoTracker from 'matomo-tracker-react-native';
import React from 'react';
import { findBestLocaleFromString } from '../utils';
import { AddressProvider } from './address_provider';
import { AnalyticsProvider } from './analytics_provider';
import { AuthProvider, TokenFunctions } from './auth_provider';
import { BuzzebeesServiceProvider, BuzzebeesServiceProviderProps } from './buzzebees_service_provider';
import { CampaignProvider } from './campaigns_provider';
import { CartProvider } from './cart_provider';
import { CategoriesProvider } from './categories_provider';
import { ConsentProvider } from './consent_provider';
import { DashboardProvider } from './dashboard_provider';
import { LoadingProvider } from './loading_indicator_provider';
import { AppLocaleProvider } from './locale_provider';
import { MaintenanceProvider } from './maintenance_provider';
import { NotificationProvider } from './notification_provider';
import { PointLogProvider } from './point_log_provider';
import { PopupProvider } from './popup_provider';
import { RegisterProvider } from './registration_provider';
import { UserProvider } from './user_provider';
import { ZipCodeProvider } from './zipcode_provider';

export type DefaultMainAppProviderProps = {
  tokenFunctions: TokenFunctions;
  defaultDashboardConfig: string;
  defaultDashboardMode: 'main' | 'sub';
  defaultMenuConfig: string;
  defaultCampaignConfig: string;
  language: string;
  matomoConfig?: MatomoConfig;
  children: React.ReactNode;
} & Omit<BuzzebeesServiceProviderProps, 'children'>;

export type MatomoConfig = {
  siteId: number;
  urlBase: string;
  trackerUrl?: string;
  userId?: string;
  disabled?: boolean;
  log?: boolean;
};

export const DefaultMainAppProvider = ({
  appId,
  appName,
  bzbsService,
  urls,
  tokenFunctions,
  defaultDashboardConfig,
  defaultDashboardMode,
  defaultMenuConfig,
  defaultCampaignConfig,
  language,
  matomoConfig,
  children,
}: DefaultMainAppProviderProps) => {
  const appLocale = findBestLocaleFromString(language ?? 'en');

  return (
    <BuzzebeesServiceProvider appId={appId} appName={appName} bzbsService={bzbsService} urls={urls}>
      <AnalyticsProvider
        instance={
          new MatomoTracker({
            siteId: matomoConfig?.siteId ?? 0,
            urlBase: matomoConfig?.urlBase ?? '',
            trackerUrl: matomoConfig?.trackerUrl,
          })
        }
      >
        <AppLocaleProvider initLocale={appLocale}>
          <LoadingProvider>
            <PopupProvider>
              <MaintenanceProvider>
                <AuthProvider tokenFunctions={tokenFunctions}>
                  <RegisterProvider>
                    <UserProvider>
                      <PointLogProvider>
                        <ConsentProvider>
                          <NotificationProvider>
                            <AddressProvider>
                              <ZipCodeProvider>
                                <CartProvider>
                                  <DashboardProvider config={defaultDashboardConfig} mode={defaultDashboardMode}>
                                    <CategoriesProvider config={defaultMenuConfig}>
                                      <CampaignProvider config={defaultCampaignConfig}>{children}</CampaignProvider>
                                    </CategoriesProvider>
                                  </DashboardProvider>
                                </CartProvider>
                              </ZipCodeProvider>
                            </AddressProvider>
                          </NotificationProvider>
                        </ConsentProvider>
                      </PointLogProvider>
                    </UserProvider>
                  </RegisterProvider>
                </AuthProvider>
              </MaintenanceProvider>
            </PopupProvider>
          </LoadingProvider>
        </AppLocaleProvider>
      </AnalyticsProvider>
    </BuzzebeesServiceProvider>
  );
};

export default DefaultMainAppProvider;
